import * as React from 'react';
import { ErrorLogEntry } from 'types/error_log_entry';
import { ErrorLogTag } from 'types/error_log_tag';
import Tag from '@magoosh/lib/tag';
import without from 'lodash/without';
import isEqual from 'lodash/isEqual';
import fetch from 'utilities/fetch';
import moment from 'moment';
import { bugsnagNotify } from 'utilities/bugsnag';
import style from './style.module.scss';
import debounce from 'lodash/debounce';
import FromNowDateText from './from_now_date_text';
import trackEvent from 'utilities/track_event';
import { find } from 'lodash';

interface Props {
  availableTags: ErrorLogTag[];
  promptId: number;
  previousEntry: ErrorLogEntry;
  examples: {
    introText: string;
    whatHappenedText: string;
    nextTimeText: string;
  };
  keepWhatHappenedOpen: boolean;
}

interface State {
  whatHappenedText: string;
  notesText: string;
  selectedTags: ErrorLogTag[];
  isSaving: boolean;
  updatedAt: string;
  mode: 'view' | 'edit' | 'help';
}

export default class ErrorLogEntryEditor extends React.Component<Props, State> {
  state: State = {
    whatHappenedText: this.props.previousEntry ? this.props.previousEntry.guidedReflectionText : '',
    notesText: this.props.previousEntry ? this.props.previousEntry.unguidedReflectionText : '',
    selectedTags: this.props.previousEntry ? this.props.previousEntry.errorLogTags : [],
    isSaving: false,
    updatedAt: this.props.previousEntry ? this.props.previousEntry.updatedAt : null,
    mode: this.props.previousEntry ? 'view' : 'edit'
  };

  render() {
    if (this.state.mode == 'help') {
      return this.renderHelpMode();
    } else if (this.state.mode == 'edit') {
      return this.renderEditMode();
    } else {
      return this.renderViewMode();
    }
  }

  renderHelpMode = () => {
    return (
      <div>
        <a className="pull-right" onClick={() => this.changeMode('edit')}>
          back to log entry
        </a>
        <br></br>
        <br></br>
        <p dangerouslySetInnerHTML={{ __html: this.props.examples.introText }}></p>
        <hr></hr>
        <h3>What a great log entry looks like</h3>
        <br></br>
        <p>
          <strong>What happened:</strong>
        </p>
        <p dangerouslySetInnerHTML={{ __html: this.props.examples.whatHappenedText }}></p>
        <br></br>
        <p>
          <strong>Next time I should:</strong>
        </p>
        <p dangerouslySetInnerHTML={{ __html: this.props.examples.nextTimeText }}></p>
      </div>
    );
  };

  renderEditMode = () => {
    return (
      <div>
        <h3>Add a Log Entry</h3>
        <br></br>
        <p>
          <strong>Categorize your entry:</strong>
        </p>
        <div>
          {this.props.availableTags.map((tag) => (
            <Tag
              key={tag.id}
              displayName={tag.displayName}
              onClickCallback={() => this.toggleTagSelected(tag)}
              isSelected={this.isTagSelected(tag)}
              canBeSelected={true}
            />
          ))}
        </div>
        <br></br>
        {this.showWhatHappenedTextbox() && (
          <React.Fragment>
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <p className={style.textEntryLabels}>
                <strong>What happened / Next time I should:</strong>
              </p>
              <a
                style={{ fontSize: '14px' }}
                onClick={() => {
                  this.changeMode('help');
                  trackEvent('error_logging_why_this');
                }}>
                why this?
              </a>
            </div>
            <textarea
              onChange={(e) => this.updateWhatHappenedText(e.target.value)}
              value={this.state.whatHappenedText}
              rows={5}
            />
            <br></br>
          </React.Fragment>
        )}
        <div>
          <p className={style.textEntryLabels}>
            <strong>Notes:</strong>
          </p>
        </div>
        <textarea
          rows={5}
          onChange={(e) => this.updateNotesText(e.target.value)}
          value={this.state.notesText}
        />
        <br></br>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}>
          <button onClick={this.clickSave} className="btn btn-primary">
            Save Entry
          </button>
          {this.state.isSaving && <small className="text-gray-light">saving...</small>}
          {this.state.updatedAt && !this.state.isSaving && (
            <small className="text-gray-light">
              saved <FromNowDateText dateToParse={this.state.updatedAt} key={this.state.updatedAt} />
            </small>
          )}
        </div>
      </div>
    );
  };

  renderViewMode = () => {
    return (
      <div>
        <h3>Log Entry</h3>
        <br></br>
        <p>
          {this.state.selectedTags.map((tag) => (
            <Tag
              key={tag.id}
              displayName={tag.displayName}
              onClickCallback={null}
              isSelected={true}
              canBeSelected={false}
            />
          ))}
        </p>
        {this.state.whatHappenedText && (
          <React.Fragment>
            <p className={style.textEntryLabels}>
              <strong>What happened / Next time I should:</strong>
            </p>
            <p className={style.entryTextContainer}>{this.state.whatHappenedText}</p>
          </React.Fragment>
        )}
        {this.state.notesText && (
          <React.Fragment>
            <p className={style.textEntryLabels}>
              <strong>Notes:</strong>
            </p>
            <p className={style.entryTextContainer}>{this.state.notesText}</p>
          </React.Fragment>
        )}
        <p className="text-gray-light">
          <small>Entry added on {moment(this.state.updatedAt).format('MMM D, YYYY')}.</small>
        </p>
        <p>
          <a onClick={() => this.changeMode('edit')}>Edit Entry</a>
        </p>
      </div>
    );
  };

  updateWhatHappenedText = (text) => {
    this.setState(
      {
        whatHappenedText: text
      },
      this.backgroundSave
    );
  };

  updateNotesText = (text) => {
    this.setState(
      {
        notesText: text
      },
      this.backgroundSave
    );
  };

  isTagSelected = (tag) => {
    if (this.deepSearchTag(tag)) {
      return true;
    }
    return false;
  };

  deepSearchTag = (tag) => {
    // Using isEqual to perform a deep comparison between objects.
    const tagFound = find(this.state.selectedTags, function (el) {
      return isEqual(el, tag);
    });

    return tagFound;
  };

  toggleTagSelected = (tag) => {
    // If tag exists in selections already, user is unchecking their selection. Otherwise they're selecting tag.
    const tagToRemove = this.deepSearchTag(tag);

    if (tagToRemove) {
      this.setState(
        {
          selectedTags: without(this.state.selectedTags, tagToRemove)
        },
        this.backgroundSave
      );
    } else {
      this.setState(
        {
          selectedTags: [...this.state.selectedTags, tag]
        },
        this.backgroundSave
      );
    }
  };

  showWhatHappenedTextbox = () => {
    if (this.props.keepWhatHappenedOpen) {
      return true;
    }

    let nonGeneralSelected = false;
    this.state.selectedTags.forEach(function (tag) {
      if (tag.slug != 'general_note') {
        nonGeneralSelected = true;
        return;
      }
    });

    return nonGeneralSelected;
  };

  changeMode = (modeToSet) => {
    this.setState({
      mode: modeToSet
    });
  };

  backgroundSave = debounce(async () => {
    this.save(false);
  }, 900);

  clickSave = async () => {
    this.save(true);
  };

  save = async (goBackToView: boolean) => {
    this.setState({
      isSaving: true
    });

    try {
      const result: ErrorLogEntry = await fetch('/api/web/error_log_entries.json', {
        method: 'POST',
        body: JSON.stringify({
          guidedReflectionText: this.state.whatHappenedText,
          unguidedReflectionText: this.state.notesText,
          errorLogTagIds: this.state.selectedTags.map((tag) => tag.id),
          promptId: this.props.promptId
        })
      });

      this.setState({
        updatedAt: result.updatedAt,
        isSaving: false,
        mode: goBackToView ? 'view' : this.state.mode
      });
    } catch (error) {
      // keep the state stuck on 'saving' for now

      // this.setState({
      //   // isSaving: false
      // });
      bugsnagNotify(error);
    }
  };
}
